import React, { ReactNode } from 'react';
import { formatCurrency } from 'utils';
import {
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { SearchOff } from '@mui/icons-material';
import { green, grey } from '@mui/material/colors';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { ProposalSummary } from '../../../dashboard/ProposalsMetricsDashboard';

interface Props {
  isLoading: boolean;
  data: ProposalSummary[];
}

export const SummaryTable = ({ data, isLoading }: Props) => {
  const columns: {
    label: string;
    footer?: React.ReactNode;
    renderKey: keyof ProposalSummary;
    formatter?: (value: any) => ReactNode;
  }[] = [
    {
      footer: 'Totals',
      label: 'Created By',
      renderKey: 'fullName',
    },
    {
      label: 'Created',
      renderKey: 'proposalsCreated',
      footer: data.reduce((acc, { proposalsCreated }) => (acc += proposalsCreated), 0),
    },
    {
      label: 'Sent',
      renderKey: 'proposalsSent',
      footer: data.reduce((acc, { proposalsSent }) => (acc += proposalsSent), 0),
    },
    {
      label: 'Accepted',
      renderKey: 'proposalsAccepted',
      footer: data.reduce((acc, { proposalsAccepted }) => (acc += proposalsAccepted), 0),
    },
    {
      renderKey: 'markupCreated',
      label: 'Markup on Created ($)',
      formatter: v => formatCurrency(v, true),
      footer: data.reduce((acc, { markupCreated }) => (acc += markupCreated), 0),
    },
    {
      renderKey: 'markupSent',
      label: 'Markup on Sent ($)',
      formatter: v => formatCurrency(v, true),
      footer: data.reduce((acc, { markupSent }) => (acc += markupSent), 0),
    },
    {
      renderKey: 'markupAccepted',
      label: 'Markup on Accepted ($)',
      formatter: v => formatCurrency(v, true),
      footer: data.reduce((acc, { markupAccepted }) => (acc += markupAccepted), 0),
    },
  ];

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: grey[50] }}>
            {columns.map(column => (
              <TableCell key={column.renderKey} align="left">
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          <RenderWithFallback
            isLoading={isLoading}
            fallBack={
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Stack direction="row" alignItems="center" justifyContent="center" gap="1rem">
                    <CircularProgress size={20} /> <Typography>Loading...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            }
          >
            <RenderWithCondition
              condition={!!data.length}
              fallBack={
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Stack direction="row" alignItems="center" justifyContent="center" gap="1rem">
                      <SearchOff />
                      <Typography>No data</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              }
            >
              {data.map(user => (
                <TableRow key={user.fullName}>
                  {columns.map(({ label, formatter, renderKey }) => (
                    <TableCell key={`${label}_${renderKey}`} align="left">
                      {typeof formatter === 'function' ? formatter(user[renderKey]) : user[renderKey]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </RenderWithCondition>
          </RenderWithFallback>
        </TableBody>

        <RenderWithCondition condition={!!data.length && !isLoading}>
          <TableFooter>
            <TableRow>
              {columns.map(({ label, footer, formatter }) => (
                <TableCell sx={{ backgroundColor: green[50] }} key={`${label}_footer`} align="left">
                  <Typography color="textPrimary" variant="body2">
                    {typeof formatter === 'function' ? formatter(footer) : footer}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </RenderWithCondition>
      </Table>
    </TableContainer>
  );
};
